import { TWPERMISSIONS } from 'dataSource/auth/types'
import jwtDecode from 'jwt-decode'

export type CustomToken = {
  permissions?: TWPERMISSIONS[]
  sub?: string
  iss?: string
  iat?: number
  exp?: number
  providerId?: string
  ehrProfileCode?: string
  sessionId?: string
}
import { CustomAccessToken, UserPermissions } from './types'

/**
 * Whether an access token can still be trusted for a front-door routing
 * decision: it decodes, carries a `permissions` array, and — if it carries
 * `exp` — that instant is in the future. A missing `exp` is accepted; a
 * present but non-numeric one is not.
 * Signature verification is the API's job; this only keeps a stale or garbled
 * cookie from painting an authenticated page before the API 401s.
 */
export const isAccessTokenUsable = (token: string): boolean => {
  try {
    const { exp, permissions } = jwtDecode<{
      exp?: unknown
      permissions?: unknown
    }>(token)

    if (!Array.isArray(permissions)) {
      return false
    }

    return (
      exp === undefined ||
      (typeof exp === 'number' &&
        Number.isFinite(exp) &&
        exp * 1000 > Date.now())
    )
  } catch {
    return false
  }
}

export const getPermissionFromToken = (token: string): TWPERMISSIONS[] => {
  try {
    const userObject: CustomAccessToken = jwtDecode(token)
    return userObject.permissions
  } catch (err) {
    return []
  }
}

export const getProviderDataFromToken = (
  token: string
): FormattedSuperAdminProviderData | null => {
  try {
    const userObject: CustomAccessToken = jwtDecode(token)

    if (!userObject.providerId || !userObject.ehrProfileCode) {
      return null
    }

    return {
      providerId: userObject.providerId,
      ehrProfileCode: userObject.ehrProfileCode,
      providerName: userObject.providerName,
    }
  } catch {
    return null
  }
}

export const getUserPermissions = (
  permissions: TWPERMISSIONS[]
): UserPermissions => {
  const isSupportUser = permissions.includes(TWPERMISSIONS.SUPPORT)
  const isSoftwareDevs = permissions.includes(TWPERMISSIONS.SOFTWARE_DEVS)
  const isClinician = permissions.includes(TWPERMISSIONS.CLINICIAN)
  const isDevOpsAdmin = permissions.includes(TWPERMISSIONS.DEVOPS_ADMIN)
  const isClinicalAdmin = permissions.includes(TWPERMISSIONS.CLINICAL_ADMIN)
  const isThirdPartyAgent = permissions.includes(
    TWPERMISSIONS.THIRD_PARTY_SUPPORT
  )
  const isSupportOrThirdPartyUser = isSupportUser || isThirdPartyAgent
  const isPatient =
    permissions.includes(TWPERMISSIONS.PATIENT) && !isSupportOrThirdPartyUser

  return {
    isSupportUser,
    isSoftwareDevs,
    isClinician,
    isDevOpsAdmin,
    isClinicalAdmin,
    isThirdPartyAgent,
    isSupportOrThirdPartyUser,
    isPatient,
  }
}
